import { useMemo, useState } from 'react';

import { CategoryBadge } from '@/components/CategoryBadge';
import { DeleteClaimModal } from '@/components/DeleteClaimModal';
import { Modal } from '@/components/Modal';
import { StatusLabel } from '@/components/StatusLabel';

import { categories } from '@/constants/categories';
import { EDITABLE_STATUSES, NON_CANCELLABLE_STATUSES } from '@/constants/status';

import { getClaimReceiptDownloadUrl } from '@/services/claims';

import type { IClaim } from '@/types/claim';

import { cn } from '@/utils/cn';
import { formatCurrency, formatDate } from '@/utils/formatting';

interface ClaimDetailsModalProps {
  claim: IClaim;
  onClose: () => void;
  onEditClick?: (claim: IClaim) => void;
}

const getReceiptFileName = (receipt: string) => {
  if (!receipt) return 'No receipt';
  const parts = receipt.split('/');
  const fileName = parts[parts.length - 1];
  const cleaned = fileName.replace(
    /_\d+_(?:[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}|[a-f0-9]{32})$/i,
    '',
  );
  return cleaned || fileName;
};

function ClaimDetailsModal({ claim, onClose, onEditClick }: ClaimDetailsModalProps) {
  const categoryInfo = categories.find((cat) => cat.id === claim.category);
  const categoryName = categoryInfo?.name || claim.categoryName || 'Unknown';
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isLoadingReceipt, setIsLoadingReceipt] = useState(false);
  const [receiptError, setReceiptError] = useState<string | null>(null);

  const hasReceipt = Boolean(claim.receipt);
  const receiptFileName = getReceiptFileName(claim.receipt);

  const handleReceiptClick = async () => {
    if (!claim.receipt || isLoadingReceipt) return;
    // Open the tab synchronously to preserve the user-gesture context.
    // Safari (and others) drop the context across an async boundary and block window.open.
    const newWindow = window.open('about:blank', '_blank');
    if (newWindow) newWindow.opener = null;
    setIsLoadingReceipt(true);
    setReceiptError(null);
    try {
      const { downloadUrl } = await getClaimReceiptDownloadUrl(claim.id, claim.receipt);
      if (newWindow) {
        newWindow.location.href = downloadUrl;
      } else {
        // Popup was blocked outright; best-effort fallback.
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Failed to get receipt download URL:', err);
      setReceiptError('Failed to load receipt. Please try again.');
      newWindow?.close();
    } finally {
      setIsLoadingReceipt(false);
    }
  };

  const canDeleteClaim = useMemo(() => {
    const status = String(claim.status || '').toLowerCase();
    return !NON_CANCELLABLE_STATUSES.has(status);
  }, [claim.status]);

  const { isApproved, showEditButton } = useMemo(() => {
    const status = String(claim.status || '').toLowerCase();
    const isApprovedStatus = status === 'approved';
    return {
      isApproved: isApprovedStatus,
      showEditButton: EDITABLE_STATUSES.has(status) || isApprovedStatus,
    };
  }, [claim.status]);

  const handleCancelSuccess = () => {
    setShowCancelModal(false);
    onClose();
  };

  const handleEditClick = () => {
    onEditClick?.(claim);
    onClose();
  };

  return (
    <Modal title="Claim Details" onClose={onClose} maxWidth="md" zIndex={60}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <p className="text-base text-gray-600">Total Amount</p>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(claim.amount, true)}</p>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-base text-gray-600">Category</p>
          <CategoryBadge categoryId={claim.category} categoryName={categoryName} />
        </div>

        {claim.details && (
          <div className="flex flex-col gap-2">
            <p className="text-base text-gray-600">Details</p>
            <p className="text-base wrap-break-word text-gray-900">{claim.details}</p>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <p className="text-base text-gray-600">Receipt</p>
            {hasReceipt ? (
              <button
                type="button"
                onClick={handleReceiptClick}
                disabled={isLoadingReceipt}
                className="flex min-w-0 flex-1 items-center justify-end gap-2 text-red-500 hover:text-red-600 disabled:cursor-not-allowed disabled:text-gray-400"
                aria-label={`View receipt ${receiptFileName}`}
                title={isLoadingReceipt ? 'Loading…' : receiptFileName}
              >
                <svg className="size-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="truncate text-sm font-medium">
                  {isLoadingReceipt ? 'Loading…' : receiptFileName}
                </span>
              </button>
            ) : (
              <span className="text-sm font-medium text-gray-500">{receiptFileName}</span>
            )}
          </div>
          {receiptError && <p className="text-right text-xs text-red-600">{receiptError}</p>}
        </div>

        <div className="flex items-center justify-between">
          <p className="text-base text-gray-600">Status</p>
          <StatusLabel status={claim.status} />
        </div>

        <div className="flex items-center justify-between">
          <p className="text-base text-gray-600">Submitted</p>
          <p className="text-base text-gray-900">{formatDate(claim.createdAt)}</p>
        </div>

        <div className="flex flex-col gap-2 pt-2">
          {showEditButton && (
            <span
              className="block w-full"
              title={isApproved ? "Approved claims can't be edited." : undefined}
            >
              <button
                type="button"
                onClick={handleEditClick}
                disabled={isApproved}
                className={cn(
                  'w-full rounded-lg px-4 py-3 font-bold transition-colors',
                  isApproved
                    ? 'cursor-not-allowed bg-red-200 text-slate-600'
                    : 'bg-red-500 text-white hover:bg-red-600',
                )}
                aria-label={
                  isApproved
                    ? "Edit claim (disabled: approved claims can't be edited)"
                    : 'Edit claim'
                }
              >
                Edit claim
              </button>
            </span>
          )}
          {canDeleteClaim && (
            <button
              type="button"
              onClick={() => setShowCancelModal(true)}
              className="w-full rounded-lg border border-red-600 px-4 py-3 font-bold text-red-600 transition-colors hover:bg-red-50"
            >
              Delete Claim
            </button>
          )}
        </div>
      </div>

      {showCancelModal && (
        <DeleteClaimModal
          claimId={claim.id}
          onCancel={() => setShowCancelModal(false)}
          onSuccess={handleCancelSuccess}
        />
      )}
    </Modal>
  );
}

export { ClaimDetailsModal };
